/// <reference types="vite/client" />
// src/api/axios.ts
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";

import { isRequestTimeout, reportRequestTimeout } from "@/sentry-report";

import type {
	MapReviewCapability,
	MapReviewQueryBody,
	MapReviewQueryEnvelope,
	MapReviewQueryMetadata,
} from "@/type/api";

export interface AxiosErrorJSON {
	message: string;
	status: number;
}
export const ErrorClient = AxiosError;

// Vite replaces `import.meta.env.DEV` with a literal at build time, so a
// production bundle carries `false` and never retries. `vite/client` types come
// from the reference above; the tsconfig declares none of its own.

/** How long the dev loop's retry waits before each attempt: a backend restart
 * under the dev supervisor (`scripts/nidus-dev.mjs`) is over in well under a
 * second, so three waits span ~1.4s and a request that survives all three is a
 * real failure. */
const DEV_RETRY_DELAYS_MS = [200, 400, 800];

/** The request config with the dev retry's attempt count attached. The field
 * lives only in this module — axios is not augmented — because it exists only
 * to remember which attempt a re-issued request is. */
interface DevRetryConfig extends AxiosRequestConfig {
	devRetryCount?: number;
}

/** True when a failure belongs to the dev loop rather than to the caller: the
 * backend is not listening (`ECONNREFUSED`/`ECONNRESET`, no HTTP response) or a
 * proxy in front of it answered 502/503/504 while it came back. The client's own
 * timeout and a caller's cancel are never re-issued, and every other status is
 * an answer from the server that a retry would only repeat. */
function isDevRetryable(error: AxiosError): boolean {
	// Read the two fields first: `isRequestTimeout` is a type predicate, and
	// narrowing through it would leave `error` as `never` below.
	const status = error.response?.status;
	const code = error.code;
	if (status === undefined) {
		return (
			!axios.isCancel(error) &&
			!isRequestTimeout(error) &&
			(code === "ECONNREFUSED" || code === "ECONNRESET")
		);
	}
	return status === 502 || status === 503 || status === 504;
}

/** How many pages a whole-collection read will follow before it stops and says
 * so. A collection with more pages than this is far larger than any list view
 * was built to hold, so the bound is the guard behind the loop's own stop
 * conditions — a server answering a page that never advances cannot spin a
 * view forever. */
const MAX_QUERY_PAGES = 100;

/** The widest page each resource admits, from its `OPTIONS` document
 * (`page.max`, plan §4.2), memoised per path: the document is a pure function
 * of the server's descriptors, so one read per resource per session answers
 * every walk. A resource that does not answer leaves `undefined`, and the walk
 * then states no page bound and takes the server's own default — narrower, and
 * no less correct. */
const pageBounds = new Map<string, Promise<number | undefined>>();

class ApiClient {
	private client: AxiosInstance;

	constructor() {
		this.client = axios.create({
			timeout: 30000,
			withCredentials: true,
		});

		// Request interceptor for auth headers, content-type, etc.
		this.client.interceptors.request.use((config) => {
			// Content-type negotiation
			config.headers["Accept"] = "application/json";
			config.headers["X-Requested-With"] = "nidus-web 0.1";

			// Add auth token if logged in
			const token = localStorage.getItem("authToken");
			if (token) {
				config.headers["Authorization"] = `Bearer ${token}`;
			}

			return config;
		});

		// Response interceptor for handling auth errors
		this.client.interceptors.response.use(
			(response) => response,
			async (error: AxiosError) => {
				if (error.response?.status === 401) {
					// Could emit event or redirect here
				}
				// The 30-second abort is produced here, not by the server, so the
				// report is made here where the request body and the operator are
				// still in hand (ts/sentry-report.ts).
				if (isRequestTimeout(error)) reportRequestTimeout(error);

				// A backend restart is a dev-loop event, not a user-visible
				// failure: a burst of ECONNREFUSED/502/503/504 while
				// scripts/nidus-dev.mjs is restarting the backend must heal
				// itself, instead of leaving the SPA on its loading screen
				// until the operator reloads. Production builds are unaffected
				// — Vite replaces `import.meta.env.DEV` with `false` at build
				// time, so this branch never runs there.
				const config = error.config as DevRetryConfig | undefined;
				const attempt = config?.devRetryCount ?? 0;
				if (
					import.meta.env.DEV &&
					config !== undefined &&
					attempt < DEV_RETRY_DELAYS_MS.length &&
					isDevRetryable(error)
				) {
					const delay = DEV_RETRY_DELAYS_MS[attempt];
					console.warn(
						`retry ${attempt + 1}/${DEV_RETRY_DELAYS_MS.length} in ` +
							`${delay}ms: ` +
							`${(config.method ?? "GET").toUpperCase()} ` +
							`${config.url ?? "(unknown endpoint)"} ` +
							`(${error.response?.status ?? error.code ?? "transport"})`,
					);
					// The retry waits out the restart window; a timeout or a
					// cancel never reaches here (isDevRetryable).
					await new Promise<void>((resolve) => setTimeout(resolve, delay));
					const retryConfig: DevRetryConfig = {
						...config,
						devRetryCount: attempt + 1,
					};
					// Re-issue through the instance, so the request interceptor
					// runs again and re-reads the auth token.
					return this.client.request(retryConfig);
				}
				return Promise.reject(error);
			},
		);
	}

	async JSONGet<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
		const response = await this.client.get<T>(url, {
			...config,
			headers: {
				Accept: "application/json",
				...config?.headers,
			},
		});
		return response.data;
	}

	async JSONPost<T = any>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig,
	): Promise<T> {
		const response = await this.client.post<T>(url, data, {
			...config,
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				...config?.headers,
			},
		});
		return response.data;
	}

	/**
	 * A QUERY request — the generic resource layer's read (RFC 10008;
	 * doc/in-progress/query.md §3.1). The method is spelled out because axios
	 * names a fixed set of verbs, and the body's media type is not optional: the
	 * server answers 415 to a QUERY that does not declare application/json.
	 * Axios sends the method through the XHR adapter unchanged, and QUERY is not
	 * one of the verbs the platform forbids.
	 */
	async JSONQuery<T>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<T> {
		const response = await this.client.request<T>({
			...config,
			url,
			method: "QUERY",
			data,
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				...config?.headers,
			},
		});
		return response.data;
	}

	/**
	 * A resource's OPTIONS request: the capability document (D7, plan §4.2) —
	 * property names, types, operators, sortability, page bounds, geometry,
	 * declared edges and static enum values. It carries no customer data and no
	 * session is needed for it, so a missing document is an ordinary failure the
	 * caller degrades from rather than an authorization error.
	 */
	async JSONOptions<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
		const response = await this.client.options<T>(url, {
			...config,
			headers: {
				Accept: "application/json",
				...config?.headers,
			},
		});
		return response.data;
	}

	/**
	 * One narrowing's whole item set, read through the envelope's own paging: the
	 * "every row" read the list views were built on, stated in the generic
	 * layer's grammar instead of fetched in one unbounded go.
	 *
	 * The walk asks for the widest page the resource admits (`OPTIONS`' `page.max`,
	 * or no bound at all when the document does not answer) and stops at the first
	 * page that comes back short of the bound the server reported — the end of the
	 * collection — or as soon as the rows in hand have consumed `filtered_total`,
	 * so a page that arrives short for some other reason cannot spin the loop.
	 * `MAX_QUERY_PAGES` is the safety net behind both, and hitting it is warned
	 * about rather than passed over in silence.
	 *
	 * The answer is one envelope over the whole narrowing: the merged items and the
	 * counts the pages reported, with `returned`/`offset`/`cursor` restated to
	 * describe the aggregate read rather than its last page.
	 */
	async JSONQueryAll<T>(
		url: string,
		body?: MapReviewQueryBody,
		config?: AxiosRequestConfig,
	): Promise<MapReviewQueryEnvelope<T>> {
		const limit = await this.queryPageMax(url);
		const items: T[] = [];
		let offset = 0;
		let answered: MapReviewQueryMetadata | undefined;
		for (let page = 1; ; page++) {
			const envelope = await this.JSONQuery<MapReviewQueryEnvelope<T>>(
				url,
				{ ...body, offset, limit },
				config,
			);
			answered = envelope.metadata;
			items.push(...envelope.items);
			offset += envelope.metadata.returned;
			if (
				envelope.metadata.returned === 0 ||
				envelope.metadata.returned < envelope.metadata.page_size ||
				offset >= envelope.metadata.filtered_total
			) {
				break;
			}
			if (page >= MAX_QUERY_PAGES) {
				console.warn(
					`query all: ${url} stopped after ${MAX_QUERY_PAGES} pages at ` +
						`${items.length} of ${envelope.metadata.filtered_total} rows`,
				);
				break;
			}
		}
		return {
			metadata: {
				...answered,
				returned: items.length,
				offset: 0,
				cursor: null,
			},
			items,
		};
	}

	/** The widest page a resource admits, read once per path from its `OPTIONS`
	 * document. */
	private queryPageMax(url: string): Promise<number | undefined> {
		let max = pageBounds.get(url);
		if (max === undefined) {
			max = this.JSONOptions<MapReviewCapability>(url)
				.then((doc) => doc?.page?.max)
				.catch(() => undefined);
			pageBounds.set(url, max);
		}
		return max;
	}

	async JSONPut<T = any>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig,
	): Promise<T> {
		const response = await this.client.put<T>(url, data, {
			...config,
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				...config?.headers,
			},
		});
		return response.data;
	}

	async JSONDelete<T = any>(
		url: string,
		config?: AxiosRequestConfig,
	): Promise<T> {
		const response = await this.client.delete<T>(url, config);
		return response.data;
	}

	async JSONPatch<T = any>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig,
	): Promise<T> {
		const response = await this.client.patch<T>(url, data, {
			...config,
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				...config?.headers,
			},
		});
		return response.data;
	}
}

// Single instance export - this IS the singleton
export const apiClient = new ApiClient();
